<template>
  <div class="source-content polls">
    <div class="filter-bar">
      <search-filter
        :value="search"
        :options="allTags"
        :selected="selectedTags"
        data-test="producer-assets-displayElements-searchFilter"
        @input="changePollsSearch"
        @update:selected="onUpdateSelectedTags"
      />
    </div>

    <!-- No polls after searching -->
    <div v-if="!isLoading && !total" class="text-center">
      <h3 class="margin-bottom-md">
        No polls found
      </h3>

      <button
        class="tb-btn tb-btn-contained medium-em"
        data-test="producer-assets-displayElements-resetSearch"
        @click="clearTags"
      >
        Reset Search
      </button>
    </div>

    <div
      ref="sourceList"
      class="source-list"
      data-test="producer-assets-displayElements-list"
    >
      <div
        v-for="poll in filteredPolls"
        class="poll-collapse"
      >
        <button
          :key="poll.pollId"
          class="poll-collapse__button"
          data-test="producer-assets-displayElements-addNewPoll"
          @click="pollCollapsedState[poll.pollId] = !pollCollapsedState[poll.pollId]"
        >
          <span
            class="icon margin-right-xs"
            :class="pollCollapsedState[poll.pollId] ? 'icon-caret-right' : 'icon-caret-down'"
          />
          <div class="poll-collapse__info">
            <div>{{ poll.title }}</div>
          </div>
          <div class="poll-collapse__displays">
            {{ poll.displays?.length || 0 }} displays
          </div>
        </button>
        <div
            :class="{
              'source-item-tags': true,
              'margin-top-xxs': poll.tags?.length,
              'margin-left-xl': poll.tags?.length,
            }"
            :data-test="`producer-assets-element-tags:${poll.pollId}`"
          >
            <span
              v-for="tag in poll.tags"
              :key="tag"
              class="tag"
              :data-test="`producer-assets-element-tags:${poll.pollId}_${tag.toLowerCase()}`"
            >
              {{ tag.toLowerCase() }}
            </span>
          </div>
        <div
          v-show="!pollCollapsedState[poll.pollId]"
          :key="`displays-${poll.pollId}`"
          class="margin-left-md margin-top-sm"
        >
          <div v-if="!poll.displays.length" class="text-center">
            <em>
              Add your first <strong>On Screen Layout</strong> in Creator to start showing this poll and results.
            </em>
          </div>
          <draggable
            v-else
            :list="poll.displays"
            :clone="clone"
            :sort="false"
            animation="200"
            :group="{ name: 'timeline', pull: 'clone', put: false }"
            revert-on-spill="true"
            @start="startDrag"
            @end="$store.dispatch('clearDragType')"
          >
            <div
              v-for="display in poll.displays"
              class="source-item poll"
              :data-template-id="display.displayId"
              :data-test="`producer-assets-element:${display.displayId}`"
              :key="display.displayId"
            >
              <div class="tb-checkbox">
                <input
                  type="checkbox"
                  :id="display.displayId"
                  :value="display"
                  v-model="selected"
                  :data-test="`producer-assets-element-element:${display.displayId}`"
                >

                <label class="checkbox" :for="display.displayId" />
              </div>

              <div class="source-item-wrapper">
                <img class="preview margin-right-xs" :src="display.previewUrl" :data-test="`producer-assets-element-element-preview:${display.displayId}`" />

                <div class="source-item-info-wrapper">
                  <div class="source-title title-text" :data-test="`producer-assets-element-title:${display.displayId}`">
                    <span v-if="display.displayType === 'poll'" class="fak fa-hand-point-click margin-right-xxs color-highlight-blue text-sm" />
                    {{ display.title }}
                  </div>
                </div>

                <div class="source-description description-text capitalize" :data-test="`producer-assets-element-layout:${display.displayId}`">
                  {{ display.displayType }}
                </div>

                <div class="source-actions margin-left-xxs">
                  <button
                    class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                    title="Add to playlist"
                    :data-test="`producer-assets-element-addToPlaylist:${display.displayId}`"
                    @click="addToPlaylist(display, 'poll')"
                  >
                    <span class="icon icon-plus" />
                  </button>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </div>

      <div ref="loadTrigger" class="load-trigger">
        <div v-if="isLoading" class="loading loading--inline" data-test="producer-assets-displayElements-loading">
          <i class="icon icon-spinner"></i>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="flex content-end items-center padding-md"
      data-test="producer-assets-selected"
    >
      <p class="text-primary margin-bottom-none margin-right-sm" data-test="producer-assets-selected-count">{{ selected.length }} selected</p>

      <button
        class="tb-btn tb-btn-contained medium-em margin-left-md"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-test="producer-assets-selected-addToPlaylist"
        @click="addSelectedToPlaylist('polls')"
      >
        Add to playlist
      </button>
    </div>
  </div>
</template>

<script>
import * as uuid from 'uuid';
import { mapState, mapActions } from 'vuex';
import { pick } from 'lodash-es';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import tabMixins from '../../../mixins/tab-mixins';

import toast from '../../../components/toast/toast';

export default {
  mixins: [tabMixins],

  components: {
    SearchFilter,
  },
  data() {
    return {
      selected: [],
      selectedTags: [],
      pollCollapsedState: {},
    };
  },

  created() {
    try {
      this.getPolls();
      this.getPollTags();
    } catch (error) {
      console.error('Failed to load polls:', error);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load polls.',
      });
    }
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  watch: {
    polls(newValue) {
      newValue.forEach((poll) => {
        if (!this.pollCollapsedState[poll.pollId]) {
          this.$set(this.pollCollapsedState, poll.pollId, true);
        }
      });

      if (!this.$refs.sourceList || !this.polls.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },

  methods: {
    ...mapActions('polls', [
      'getPolls',
      'changePollsSearch',
      'resetPolls',
      'incrementPollsPage',
      'getPollTags',
    ]),

    onUpdateSelectedTags(tags) {
      this.selectedTags = tags.map(t => t.toLowerCase());
    },

    clearTags() {
      this.selectedTags = [];
      this.changePollsSearch('');
    },

    /**
    * Attempt to load more polls
    */
    loadMore() {
      if (!this.isLoading && this.$store.getters.hasMorePolls) {
        this.incrementPollsPage();
      }
    },

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t.pollId === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'poll',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    clone(original) {
      const clone = pick(original, ['pollId', 'displayId', 'title', 'displayType', 'previewUrl']);
      clone.layout = 'poll';
      clone.posts = [];
      clone.fields = {};
      clone.timeline_id = uuid.v1();
      clone.displayTime = 30000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      return clone;
    },

    reloadPolls() {
      this.resetPolls();
      this.getPolls();
      this.getPollTags();
    },
  },

  computed: {
    ...mapState('polls', [
      'search',
      'tags',
      'isLoading',
      'total',
      'polls',
      'allTags',
    ]),

    filteredPolls() {
      return this.polls.filter((poll) => {
        if (this.selectedTags.length) {
          const pollTags = (poll.tags || []).map(tag => tag.toLowerCase());
          return this.selectedTags.some(tag => pollTags.includes(tag)); // OR logic
        }

        return true;
      }).map((poll) => {
        poll.displays = poll.displays.map((display) => { // eslint-disable-line no-param-reassign
          if (display.displayType === 'poll') {
            // Don't show interactive poll displays in producer.
            return false;
          }

          // Ensure pollId is set so it's available to clone
          display.pollId = poll.pollId; // eslint-disable-line no-param-reassign
          return display;
        }).filter(Boolean);

        return poll;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.poll-collapse {
  & + & {
    margin-top: 1rem;
  }

  &__button {
    display: flex;
    align-items: start;
    width: 100%;
    background-color: transparent;
    border: none;
    padding: 0.5rem;
    color: white;
    text-align: left;
  }

  &__info {
    flex-grow: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__displays {
    margin-left: 1rem;
    color: #7b7b7b;
    font-style: italic;
    text-wrap: nowrap;
  }
}
</style>
